import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Home } from "./pages/Home";
import { FindTutor } from "./pages/FindTutor";
import { TutorProfile } from "./pages/TutorProfile";
import { FindOrganisation } from "./pages/FindOrganisation";
import { FindJob } from "./pages/FindJob";
import { Register } from "./pages/auth/Register";
import { Login } from "./pages/auth/Login";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { Dashboard } from "./pages/Dashboard";
import { StudentDashboard } from "./pages/dashboards/StudentDashboard";
import { TutorDashboard } from "./pages/dashboards/TutorDashboard";
import { OrganizationDashboard } from "./pages/dashboards/OrganizationDashboard";
import { CreateStudentProfile } from "./pages/profiles/CreateStudentProfile";
import { EnhancedStudentProfile } from "./pages/profiles/EnhancedStudentProfile";
import { CreateTutorProfile } from "./pages/profiles/CreateTutorProfile";
import { EnhancedTutorProfile } from "./pages/profiles/EnhancedTutorProfile";
import { CreateOrganizationProfile } from "./pages/profiles/CreateOrganizationProfile";
import TutorProfileDetail from "./pages/profiles/detail/TutorProfileDetailEnhanced";
import { OrganizationProfileDetail } from "./pages/profiles/detail/OrganizationProfileDetail";
import { AdminDashboard } from "./pages/admin/AdminDashboard";
import { CreateTuitionPost } from "./pages/tuition/CreateTuitionPost";
import { BrowseTuitionPosts } from "./pages/tuition/BrowseTuitionPosts";
import { MyTuitionPosts } from "./pages/tuition/MyTuitionPosts";
import { TuitionPostDetail } from "./pages/tuition/TuitionPostDetail";
import { JobDetail } from "./pages/jobs/JobDetail";
import { CreateJob } from "./pages/jobs/CreateJob";
import { Toaster } from "./components/ui/sonner";
import { WalletPage } from "./pages/WalletPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppContent() {
  const { user } = useAuth();

  return (
    <>
      <Header />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/find-tutor" element={<FindTutor />} />
        <Route path="/tutors/:tutorId" element={<TutorProfileDetail />} />
        <Route
          path="/jobs/create"
          element={
            <ProtectedRoute allowedRoles={["organization"]}>
              <CreateJob />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/:jobId/edit"
          element={
            <ProtectedRoute allowedRoles={["organization"]}>
              <CreateJob editMode={true} />
            </ProtectedRoute>
          }
        />

        <Route path="/organizations/:orgId" element={<OrganizationProfileDetail />} />

        <Route path="/tutor/:id" element={<TutorProfile />} />
        <Route path="/find-organisation" element={<FindOrganisation />} />
        <Route path="/find-job" element={<FindJob />} />
        <Route path="/jobs/:jobId" element={<JobDetail />} />

        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Tuition Posts Routes */}
        <Route path="/tuition-posts" element={<BrowseTuitionPosts />} />

        {/* Redirect singular to plural */}
        <Route path="/tuition-post/create" element={<Navigate to="/tuition-posts/create" replace />} />

        <Route
          path="/tuition-posts/create"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <CreateTuitionPost />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tuition-posts/my"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <MyTuitionPosts />
            </ProtectedRoute>
          }
        />
        <Route path="/tuition-posts/:postId" element={<TuitionPostDetail />} />


        {/* Protected Routes */}
        <Route
          path="/wallet"
          element={
            <ProtectedRoute>
              <WalletPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        {/* Role-specific Dashboards */}
        <Route
          path="/dashboard/student"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <StudentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/tutor"
          element={
            <ProtectedRoute allowedRoles={["tutor"]}>
              <TutorDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/organization"
          element={
            <ProtectedRoute allowedRoles={["organization"]}>
              <OrganizationDashboard />
            </ProtectedRoute>
          }
        />

        {/* Profile Routes */}
        <Route
          path="/profile/create"
          element={
            <ProtectedRoute allowedRoles={["student", "tutor", "organization"]}>
              {user?.role === "student" ? (
                <EnhancedStudentProfile />
              ) : user?.role === "tutor" ? (
                <EnhancedTutorProfile />
              ) : (
                <CreateOrganizationProfile />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile/edit"
          element={
            <ProtectedRoute allowedRoles={["student", "tutor", "organization"]}>
              {user?.role === "student" ? (
                <EnhancedStudentProfile editMode={true} />
              ) : user?.role === "tutor" ? (
                <EnhancedTutorProfile editMode={true} />
              ) : (
                <CreateOrganizationProfile editMode={true} />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Navigate to="/profile/edit" replace />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />

        {/* Tuition Post Edit Route */}
        <Route
          path="/tuition-posts/:postId/edit"
          element={
            <ProtectedRoute allowedRoles={["student"]}>
              <CreateTuitionPost editMode={true} />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
      </Routes>
      {!user && <Footer />}
      <Toaster />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
